import * as React from "react";
import { Avatar as AvatarPrimitive } from "radix-ui";
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils";

type AvatarVariant = "user" | "entity";
type AvatarSize =
  | "5"
  | "6"
  | "8"
  | "10"
  | "12"
  | "sm"
  | "default"
  | "lg";

function resolveAvatarSize(size: AvatarSize | undefined): "5" | "6" | "8" | "10" | "12" {
  if (size === "sm") return "8";
  if (size === "default") return "10";
  if (size === "lg") return "12";
  return size ?? "10";
}

const avatarVariants = cva(
  "group/avatar relative flex shrink-0 overflow-hidden bg-muted text-muted-foreground select-none",
  {
    variants: {
      size: {
        "5": "size-5",
        "6": "size-6",
        "8": "size-8",
        "10": "size-10",
        "12": "size-12",
      },
      variant: {
        user: "rounded-full",
        entity: "",
      },
    },
    compoundVariants: [
      { variant: "entity", size: "5", class: "rounded-sm" },
      { variant: "entity", size: "6", class: "rounded-sm" },
      { variant: "entity", size: "8", class: "rounded-md" },
      { variant: "entity", size: "10", class: "rounded-lg" },
      { variant: "entity", size: "12", class: "rounded-lg" },
    ],
    defaultVariants: {
      size: "10",
      variant: "user",
    },
  },
);

type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: AvatarSize;
  variant?: AvatarVariant;
};

function Avatar({
  className,
  size = "10",
  variant = "user",
  ...props
}: AvatarProps) {
  const resolvedSize = resolveAvatarSize(size);
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      data-variant={variant}
      className={cn(avatarVariants({ size: resolvedSize, variant }), className)}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(
        "aspect-square size-full rounded-[inherit] object-cover",
        className,
      )}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "flex size-full items-center justify-center rounded-[inherit] bg-muted font-medium text-muted-foreground group-data-[size=10]/avatar:text-sm group-data-[size=12]/avatar:text-sm group-data-[size=5]/avatar:text-[9px] group-data-[size=6]/avatar:text-[10px] group-data-[size=8]/avatar:text-xs [&_svg:not([class*='size-'])]:size-1/2",
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "group/avatar-group flex items-center -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background",
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground ring-2 ring-background select-none group-has-data-[size=10]/avatar-group:size-10 group-has-data-[size=10]/avatar-group:text-sm group-has-data-[size=12]/avatar-group:size-12 group-has-data-[size=12]/avatar-group:text-sm group-has-data-[size=5]/avatar-group:size-5 group-has-data-[size=5]/avatar-group:text-[9px] group-has-data-[size=6]/avatar-group:size-6 group-has-data-[size=6]/avatar-group:text-[10px] group-has-data-[size=8]/avatar-group:size-8 group-has-data-[size=8]/avatar-group:text-xs [&_svg:not([class*='size-'])]:size-1/2",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Extract initials from a name string.
 *
 * Takes the first letter of each word, uppercased, max 2 characters.
 */
const getAvatarFallback = (name: string) => {
  if (!name) {
    return "";
  }
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  getAvatarFallback,
};
export type { AvatarVariant, AvatarSize, AvatarProps };
